import React, { useEffect, useState, useRef } from "react";
import { getServices } from "../../apiServices/apiServices";
import toast from "react-hot-toast";
import { Link, useLocation, useNavigate } from "react-router-dom";
import LoadingGif from "../../assets/loading_anim.gif";
import { FiSearch } from "react-icons/fi";
import { jwtDecode } from "jwt-decode";
import { Card, Typography } from "@material-tailwind/react";
import { Select, Option } from "@material-tailwind/react";

function HeadDashboard() {
  const [branchData, setBranchData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchField, setSearchField] = useState("");
  const [filter, setFilter] = useState("all");
  const [regionalFilter, setRegionalFilter] = useState("all");
  const access_token =
    localStorage.getItem("gassol-head-branch-token") ||
    localStorage.getItem("gassol-admin-token");
  const decoded = jwtDecode(access_token);
  const intervalRef = useRef(null);
  const location = useLocation().state;
  const navigate = useNavigate();

  const fetchBrachRealtimeData = async (load_bool) => {
    setLoading(load_bool);
    try {
      const url =
        decoded.role === 3
          ? searchField
            ? `/api/device/current/data?search=${searchField}`
            : `/api/device/current/data`
          : searchField
          ? `/api/device/current/data?search=${searchField}&headbranch_id=${location.head_office?._id}`
          : `/api/device/current/data?headbranch_id=${location.head_office?._id}`;

      const res = await getServices(url, access_token);

      setLoading(false);
      if (res.responseCode === 200) {
        setBranchData(res.responseData);
      } else {
        setBranchData([]);
      }
    } catch (error) {
      toast.error(error);
      setLoading(false);
    }
  };

  // Start interval for realtime updates
  const startInterval = () => {
    intervalRef.current = setInterval(() => {
      if (!searchField) {
        fetchBrachRealtimeData(false);
      }
    }, 5000);
  };

  // Clear interval
  const clearDataInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    // Initial fetch
    fetchBrachRealtimeData(true);

    // Start interval
    startInterval();

    // Cleanup
    return () => {
      clearDataInterval();
    };
  }, []);

  useEffect(() => {
    // When searchField changes, we need to handle the interval
    if (searchField) {
      clearDataInterval();
      fetchBrachRealtimeData(true);
    } else {
      clearDataInterval();
      fetchBrachRealtimeData(true);
      startInterval();
    }
  }, [searchField]);

  const handleBranchSearch = async (e) => {
    const value = e.target.value;
    setSearchField(value);
  };

  // Get unique regional office names
  const uniqueBranches = [
    ...new Set(branchData.map((branch) => branch.ogHeadbranch)),
  ];

  // Combine status filter (Bypassing/Offline/Idle) and regional filter
  const filteredBranchData = branchData
    .filter((item) => {
      const isOffline = item.status?.is_online === 0;
      const isBypassing =
        item.status?.isBypassing === 1 && item.status?.is_online === 1;
      const isIdle =
        (item.status?.mf_off === 1 ||
          (item.status?.is_online && item.status?.is_online !== 0)) &&
        item.status?.isBypassing === 0;

      switch (filter) {
        case "Bypassing":
          return isBypassing;
        case "Device Offline":
          return isOffline;
        case "Device Online":
          return isIdle;
        default:
          return true; // "all"
      }
    })
    .filter((item) => {
      // Apply regional filter
      if (regionalFilter === "all") return true;
      return item.ogHeadbranch === regionalFilter;
    });

  // / Calculate counts based on FILTERED data
  const bypassingCount = filteredBranchData.filter(
    (item) => item.status?.isBypassing === 1 && item.status?.is_online === 1
  ).length;

  const offlineCount = filteredBranchData.filter(
    (item) => item.status?.is_online === 0
  ).length;

  // Add Device Online count
  const onlineCount = filteredBranchData.filter((item) => {
    const isOffline = item.status?.is_online === 0;
    const isBypassing =
      item.status?.isBypassing === 1 && item.status?.is_online === 1;
    const isIdle =
      (item.status?.mf_off === 1 ||
        (item.status?.is_online && item.status?.is_online !== 0)) &&
      item.status?.isBypassing === 0;

    return !isOffline && !isBypassing && isIdle;
  }).length;

  const totalDevicesCount = filteredBranchData.length;

  return (
    <>
      <div className="px-6 pb-5 flex flex-col gap-6 pt-4 lg:pt-0">
        {/* Title */}
        <p className="text-4xl font-bold">
          {decoded.role === 3 ? "Dashboard" : "Sub-branches"}
        </p>
        <div className="flex justify-between items-center">
          <div className="flex flex-col gap-4">
            <div className="relative">
              <input
                type="text"
                className="py-4 pl-12 pr-4 outline-none rounded-xl w-full md:min-w-[280px] xl:min-w-[350px]"
                placeholder="Search by Branch name"
                style={{
                  boxShadow:
                    "0 4px 8px 0 rgba(0, 0, 0, 0), 0 6px 80px 0 rgba(0, 0, 0, 0.19)",
                }}
                onChange={handleBranchSearch}
                value={searchField}
              />
              <FiSearch className="absolute top-5 left-4 text-[#BDBDBD]" />
            </div>
            <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
              <p>Bypassing:{bypassingCount}</p>
              <p>Device Offline: {offlineCount}</p>
              <p>Device Online: {onlineCount}</p>
              <p>Total Devices: {totalDevicesCount}</p>
            </div>
          </div>

          <div>
            <div className="flex-col md:flex-row flex items-center gap-4 mb-5 md:mb-3">
              <div className="flex flex-col">
                <label className="font-poppins mb-1">Status</label>
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="font-poppins border border-gray-300 rounded-lg px-4 py-2"
                >
                  <option value="all">All</option>
                  <option value="Bypassing">Bypassing</option>
                  <option value="Device Offline">Device Offline</option>
                  <option value="Device Online">Device Online</option>
                </select>
              </div>

              {/* Regional Filter */}
              {uniqueBranches.length > 1 && (
                <div className="flex flex-col">
                  <label className="font-poppins mb-1">Regional</label>
                  <select
                    value={
                      uniqueBranches.includes(regionalFilter)
                        ? regionalFilter
                        : "all"
                    }
                    onChange={(e) => setRegionalFilter(e.target.value)}
                    className="font-poppins border border-gray-300 rounded-lg px-4 py-2"
                  >
                    <option value="all">All</option>
                    {uniqueBranches.map((uniqueBranch) => (
                      <option key={uniqueBranch} value={uniqueBranch}>
                        {uniqueBranch}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            {/* Stats */}

            {/* Status Filter */}
          </div>
        </div>

        {/* {!loading ? (
          <>
            {branchData.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {branchData.map((item, i) => (
                  <Link
                    className="bg-white drop-shadow-xl relative p-4 rounded-xl border"
                    key={i}
                    to={
                      decoded.role === 3
                        ? `/head_office/controller_details`
                        : "/admin/admin_controller_details"
                    }
                    state={{
                      sub_branch_id: item.subbranchName?.id,
                      subbranchName: item.subbranchName?.name,
                      hardware_id: item.status?.hardware_id,
                      controller_data: {
                        branch: item.subbranchName?.name,
                        company_name: item?.headbranch,
                      },
                    }}
                  >
                    <p
                      className={`absolute -top-3 left-1/2 transform -translate-x-1/2 text-center bg-white px-2 font-semibold text-[15px] capitalize whitespace-nowrap ${
                        item.status?.is_online === 0
                          ? "text-red-500"
                          : item.status?.manifold == null
                          ? "text-red-500"
                          : ""
                      }`}
                    >
                      {i +
                        1 +
                        ". " +
                        item.subbranchName?.name +
                        " " +
                        item.status?.hardware_id?.slice(-4) ?? "-"}
                    </p>
                    <div className="flex flex-col gap-2">
                      <div className="flex justify-between items-center text-sm">
                        <p>Active Manifold:</p>
                        <p
                          className={`font-semibold text-right ${
                            item.status?.is_online === 0
                              ? "text-red-500"
                              : item.status?.isBypassing === 1
                              ? "text-red-500"
                              : item.status?.manifold == null
                              ? "text-red-500"
                              : ""
                          }`}
                        >
                          {item.status?.is_online === 1
                            ? item.status?.isBypassing === 1
                              ? "Bypassing"
                              : item.status?.manifold === 1
                              ? "Manifold 1"
                              : item.status?.manifold === 2
                              ? "Manifold 2"
                              : item.status?.manifold === null
                              ? "None"
                              : ""
                            : "Device Offline"}
                        </p>
                      </div>

                      <div className="flex justify-between items-center text-sm">
                        <p>Manifold 1:</p>
                        <p
                          className={`font-semibold ${
                            item.status?.isBypassing === 1
                              ? "text-red-500"
                              : item.status?.mf1_st == 0
                              ? "text-red-500"
                              : item.status?.mf1_st == 1
                              ? "text-green-700"
                              : ""
                          }`}
                        >
                          {item.status?.isBypassing === 1
                            ? "N/A"
                            : item.status?.mf1_st == 0
                            ? "Empty"
                            : item.status?.mf1_st == 1
                            ? "Loaded"
                            : item.status?.mf1_st == 2
                            ? "Running"
                            : item.status?.mf1_st == 3
                            ? "Emptying"
                            : "-"}
                        </p>
                      </div>

                      <div className="flex justify-between items-center text-sm">
                        <p>Manifold 2:</p>
                        <p
                          className={`font-semibold ${
                            item.status?.isBypassing === 1
                              ? "text-red-500"
                              : item.status?.mf2_st == 0
                              ? "text-red-500"
                              : item.status?.mf2_st == 1
                              ? "text-green-700"
                              : ""
                          }`}
                        >
                          {item.status?.isBypassing === 1
                            ? "N/A"
                            : item.status?.mf2_st == 0
                            ? "Empty"
                            : item.status?.mf2_st == 1
                            ? "Loaded"
                            : item.status?.mf2_st == 2
                            ? "Running"
                            : item.status?.mf2_st == 3
                            ? "Emptying"
                            : "-"}
                        </p>
                      </div>

                      <div className="flex justify-between items-center text-sm">
                        <p>Pressure:</p>
                        <p className="font-semibold">
                          {item.status?.pressure ?? "-"}
                        </p>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <p>Signal Strength:</p>
                        <p className="font-semibold">
                          {item.status?.signal ?? "-"}
                        </p>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <p>Temperature:</p>
                        <p className="font-semibold">
                          {item.status?.temp ?? "-"}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center h-[50vh] flex flex-col justify-center items-center">
                <p className="font-semibold text-xl sm:text-3xl">
                  Oops! No Branch Found...
                </p>
                <p className="text-sm mt-2 ">
                  Looks like we couldn't find any branch data that match your
                  search right now.
                </p>
              </div>
            )}
          </>
        ) : (
          <div className="w-full flex justify-center items-center h-[60vh]">
            <img src={LoadingGif} alt="" />
          </div>
        )} */}

        {!loading ? (
          <>
            {/* {branchData.length > 0 ? ( */}
            {filteredBranchData.length > 0 ? (
              <Card
                className="h-full w-full rounded-none overflow-x-scroll md:overflow-x-hidden"
                style={{
                  boxShadow:
                    "0 4px 8px 0 rgba(0, 0, 0, 0), 0 6px 20px 0 rgba(0, 0, 0, 0.06)",
                }}
              >
                <table className="min-w-full bg-white border rounded-lg">
                  <thead>
                    <tr>
                      {[
                        "#",
                        "Branch Name",
                        // "Hardware ID",
                        "Active Manifold",
                        "Manifold 1",
                        "Manifold 2",
                        "Pressure",
                        "Signal Strength",
                        "Temperature",
                        // "Action",
                      ].map((header, index) => (
                        <th
                          key={index}
                          className="border-b border-blue-gray-100 bg-[#03438D] p-4"
                        >
                          <Typography
                            variant="small"
                            color="blue-gray"
                            className="font-poppins text-white font-semibold uppercase text-left"
                          >
                            {header}
                          </Typography>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* {branchData.map((item, i) => { */}
                    {filteredBranchData.map((item, i) => {
                      const isOffline = item.status?.is_online === 0;
                      const isBypassing = item.status?.isBypassing === 1;
                      const navigationPath =
                        decoded.role === 3
                          ? "/head_office/controller_details"
                          : "/admin/admin_controller_details";

                      const navigationState = {
                        sub_branch_id: item.subbranchName?.id,
                        subbranchName: item.subbranchName?.name,
                        hardware_id: item.status?.hardware_id,
                        controller_data: {
                          branch: item.subbranchName?.name,
                          company_name: item?.headbranch,
                        },
                      };

                      return (
                        <tr
                          key={i}
                          className="hover:bg-gray-100 cursor-pointer"
                          onClick={() =>
                            navigate(navigationPath, { state: navigationState })
                          }
                        >
                          <td className="p-4">
                            <Typography
                              variant="small"
                              color="blue-gray"
                              className="font-poppins font-semibold"
                            >
                              {i + 1}
                            </Typography>
                          </td>
                          <td className="p-4">
                            <Typography
                              variant="small"
                              color="blue-gray"
                              className="font-poppins font-semibold"
                            >
                              {item.subbranchName?.name || "-"}
                            </Typography>
                          </td>
                          {/* <td className="p-4">
                            <Typography
                              variant="small"
                              color="blue-gray"
                              className="font-poppins font-semibold"
                            >
                              {item.status?.hardware_id?.slice(-4) ?? "-"}
                            </Typography>
                          </td> */}
                          <td className="p-4">
                            <Typography
                              variant="small"
                              color="blue-gray"
                              className={`font-poppins font-semibold ${
                                isOffline
                                  ? "text-red-500"
                                  : isBypassing
                                  ? "text-red-500"
                                  : item.status?.manifold == null
                                  ? "text-red-500"
                                  : ""
                              }`}
                            >
                              {isOffline
                                ? "Device Offline"
                                : isBypassing
                                ? "Bypassing"
                                : item.status?.mf_off == 1 && decoded.role == 2
                                ? "Idle"
                                : item.status?.manifold === 1
                                ? "Manifold 1"
                                : item.status?.manifold === 2
                                ? "Manifold 2"
                                : item.status?.manifold === null
                                ? "None"
                                : ""}
                            </Typography>
                          </td>
                          {/* Manifold 1 status */}
                          <td className="p-4">
                            <Typography
                              variant="small"
                              color="blue-gray"
                              className={`font-poppins font-semibold ${
                                isOffline
                                  ? "text-red-500"
                                  : isBypassing
                                  ? "text-red-500"
                                  : item.status?.mf1_st == 0
                                  ? "text-red-500"
                                  : item.status?.mf1_st == 1
                                  ? "text-green-700"
                                  : "text-black"
                              }`}
                            >
                              {/* {isBypassing
                                ? "N/A"
                                : item.status?.mf1_st == 0
                                ? "Empty"
                                : item.status?.mf1_st == 1
                                ? "Loaded"
                                : item.status?.mf1_st == 2
                                ? "Running"
                                : item.status?.mf1_st == 3
                                ? "Emptying"
                                : "-"} */}
                              {isOffline && decoded.role !== 2
                                ? "N/A"
                                : isBypassing && decoded.role !== 2
                                ? "N/A"
                                : item.status?.mf1_st == 0
                                ? "Empty"
                                : item.status?.mf1_st == 1
                                ? "Loaded"
                                : item.status?.mf1_st == 2
                                ? "Running"
                                : item.status?.mf1_st == 3
                                ? "Emptying"
                                : "-"}
                            </Typography>
                          </td>
                          {/* Manifold 2 status */}
                          <td className="p-4">
                            <Typography
                              variant="small"
                              color="blue-gray"
                              className={`font-poppins font-semibold ${
                                isOffline
                                  ? "text-red-500"
                                  : isBypassing
                                  ? "text-red-500"
                                  : item.status?.mf2_st == 0
                                  ? "text-red-500"
                                  : item.status?.mf2_st == 1
                                  ? "text-green-700"
                                  : "text-black"
                              }`}
                            >
                              {/* {isBypassing
                                ? "N/A"
                                : item.status?.mf2_st == 0
                                ? "Empty"
                                : item.status?.mf2_st == 1
                                ? "Loaded"
                                : item.status?.mf2_st == 2
                                ? "Running"
                                : item.status?.mf2_st == 3
                                ? "Emptying"
                                : "-"} */}
                              {isOffline && decoded.role !== 2
                                ? "N/A"
                                : isBypassing && decoded.role !== 2
                                ? "N/A"
                                : item.status?.mf2_st == 0
                                ? "Empty"
                                : item.status?.mf2_st == 1
                                ? "Loaded"
                                : item.status?.mf2_st == 2
                                ? "Running"
                                : item.status?.mf2_st == 3
                                ? "Emptying"
                                : "-"}
                            </Typography>
                          </td>
                          {/* Pressure */}
                          <td className="p-4">
                            <Typography
                              variant="small"
                              color="blue-gray"
                              className="font-poppins font-semibold"
                            >
                              {item.status?.pressure ?? "-"}
                            </Typography>
                          </td>
                          {/* Signal */}
                          <td className="py-2 px-4 text-center">
                            <Typography
                              variant="small"
                              color="blue-gray"
                              className="font-poppins font-semibold"
                            >
                              {item.status?.signal ?? "-"}
                            </Typography>
                          </td>
                          {/* Temperature */}
                          <td className="p-4">
                            <Typography
                              variant="small"
                              color="blue-gray"
                              className="font-poppins font-semibold"
                            >
                              {item.status?.temp ?? "-"}
                            </Typography>
                          </td>
                          {/* Action link */}
                          {/* <td className="py-2 px-4 text-center">
                            <Link
                              to={
                                decoded.role === 3
                                  ? `/head_office/controller_details`
                                  : "/admin/admin_controller_details"
                              }
                              state={{
                                sub_branch_id: item.subbranchName?.id,
                                subbranchName: item.subbranchName?.name,
                                hardware_id: item.status?.hardware_id,
                                controller_data: {
                                  branch: item.subbranchName?.name,
                                  company_name: item?.headbranch,
                                },
                              }}
                              className="text-blue-600 underline"
                            >
                              View Details
                            </Link>
                          </td> */}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Card>
            ) : (
              <div className="text-center h-[50vh] flex flex-col justify-center items-center">
                <p className="font-semibold text-xl sm:text-3xl">
                  Oops! No Branch Found...
                </p>
                <p className="text-sm mt-2 ">
                  Looks like we couldn't find any branch data that match your
                  search right now.
                </p>
              </div>
            )}
          </>
        ) : (
          <div className="w-full flex justify-center items-center h-[60vh]">
            <img src={LoadingGif} alt="Loading..." />
          </div>
        )}
      </div>
    </>
  );
}

export default HeadDashboard;
